import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useLocation } from "react-router-dom";

import { formatMoney } from "../../formatters";
import useDevMode from "../../utils/devmode";
import { NavigationCell } from "../ClaimsTable/ui/NavigationCell";

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString();

const formatCurrency = (amount: number | null): string => {
  if (amount === null) return "-";
  return formatMoney(amount);
};

interface SuggestedMatchesModalProps {
  open: boolean;
  onClose: () => void;
  transaction: BankTransactionMessage;
  onAcceptMatch: (transactionId: string, paymentKey: string) => Promise<void>;
  onUpdateTransaction: (
    transactionUpdate: BankTransactionUpdateMessage,
  ) => Promise<void>;
  isLoading?: boolean;
}

export default function SuggestedMatchesModal({
  open,
  onClose,
  transaction,
  onAcceptMatch,
  onUpdateTransaction,
  isLoading = false,
}: SuggestedMatchesModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const devMode = useDevMode();
  const location = useLocation();

  if (!transaction) return null;
  const searchParams = new URLSearchParams(location.search);

  const hasSuggestedMatches =
    transaction.suggestedMatches && transaction.suggestedMatches.length > 0;
  const isAutoMatched = transaction.autoMatched === true;
  const noMatchesFound = transaction.noMatchesFound === true;

  const handleAcceptMatch = async (paymentKey: string) => {
    setIsSubmitting(true);
    try {
      await onAcceptMatch(transaction.wieldyId, paymentKey);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePostedOutside = async () => {
    setIsSubmitting(true);
    try {
      const transactionUpdate = {
        wieldyId: transaction.wieldyId,
        practiceId: transaction.practiceId,
        status: "POSTED_OUTSIDE",
      };

      await onUpdateTransaction(transactionUpdate);
      onClose();
    } catch (_error) {
      setIsSubmitting(false);
    }
  };

  const handleReportMissing = async () => {
    setIsSubmitting(true);
    try {
      const transactionUpdate = {
        wieldyId: transaction.wieldyId,
        practiceId: transaction.practiceId,
        status: "MISSING",
      };

      await onUpdateTransaction(transactionUpdate);
      onClose();
    } catch (_error) {
      setIsSubmitting(false);
    }
  };

  const handleMarkDuplicate = async () => {
    setIsSubmitting(true);
    try {
      const transactionUpdate = {
        wieldyId: transaction.wieldyId,
        practiceId: transaction.practiceId,
        status: "DUPLICATE",
      };

      await onUpdateTransaction(transactionUpdate);
      onClose();
    } catch (_error) {
      setIsSubmitting(false);
    }
  };

  const styles = {
    header: {
      wrapper: {
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
      },
      titleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      },
      titleBox: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
      },
      title: {
        fontSize: "1.125rem",
      },
      subtitle: {
        fontSize: "0.875rem",
        color: "text.secondary",
        mt: 1,
      },
      closeButton: {
        color: "text.secondary",
      },
    },
    table: {
      container: {
        maxHeight: "400px",
        overflowY: "auto",
      },
      headerCell: {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
      row: {
        "&:nth-of-type(odd)": {
          backgroundColor: "rgba(0, 0, 0, 0.02)",
        },
      },
      button: {
        marginRight: 1,
      },
    },
    footer: {
      wrapper: {
        display: "flex",
        justifyContent: "space-between",
        padding: "16px 24px",
        borderTop: "1px solid rgba(0, 0, 0, 0.12)",
      },
      helpText: {
        fontSize: "0.875rem",
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
      },
      iconInfo: {
        fontSize: "1rem",
        marginRight: "4px",
        color: "info.main",
      },
      buttonGroup: {
        display: "flex",
        gap: "8px",
      },
    },
    transactionDetails: {
      container: {
        padding: "16px 24px",
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        marginBottom: "16px",
      },
      row: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "8px",
      },
      label: {
        marginRight: "8px",
      },
      value: {
        textAlign: "right",
      },
    },
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "48px 24px",
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Finding matches...
          </Typography>
        </Box>
      );
    }

    if (isAutoMatched) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px",
          }}
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{ fontSize: "48px", mb: 2 }}
          />
          <Typography variant="h6" color="success.main">
            Automatically Matched!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This transaction has been matched to payment:
          </Typography>
          <Box sx={{ mt: 2, width: "100%" }}>
            <NavigationCell
              linkPath={`/payments/${transaction.paymentKey}?${searchParams}`}
              displayText={
                transaction.payerPaymentId || transaction.paymentKey || "N/A"
              }
              ariaLabel="View matched payment details"
            />
          </Box>
        </Box>
      );
    }

    if (noMatchesFound) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px",
          }}
        >
          <WarningIcon color="warning" sx={{ fontSize: "48px", mb: 2 }} />
          <Typography variant="h6">No Matches Found</Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            No matching payments were found for this transaction. You can mark
            this transaction as &quot;Posted Outside Copilot&quot;, &quot;Report
            Missing&quot;
            {devMode && <> or &quot;Duplicate&quot;</>} using the buttons below.
          </Typography>
        </Box>
      );
    }

    if (hasSuggestedMatches) {
      return (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Suggested Payment Matches
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select a payment match.
          </Typography>
          <TableContainer component={Paper} sx={styles.table.container}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.table.headerCell}>Payment ID</TableCell>
                  <TableCell sx={styles.table.headerCell}>Payer</TableCell>
                  <TableCell sx={styles.table.headerCell}>Date</TableCell>
                  <TableCell sx={styles.table.headerCell}>Amount</TableCell>
                  <TableCell sx={styles.table.headerCell}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.suggestedMatches?.map((match) => (
                  <TableRow key={match.paymentKey} sx={styles.table.row}>
                    <TableCell>
                      <NavigationCell
                        linkPath={`/payments/${match.paymentKey}?${searchParams}`}
                        displayText={match.payerPaymentId || match.paymentKey}
                        ariaLabel="View payment details"
                      />
                    </TableCell>
                    <TableCell>{match.payer || "N/A"}</TableCell>
                    <TableCell>
                      {match.paymentDate
                        ? formatDate(match.paymentDate)
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {match.paymentAmount !== undefined
                        ? formatCurrency(match.paymentAmount)
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleAcceptMatch(match.paymentKey)}
                        disabled={isSubmitting}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={16} />
                          ) : (
                            <CheckCircleOutlineIcon />
                          )
                        }
                      >
                        Confirm
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "24px",
        }}
      >
        <WarningIcon color="warning" sx={{ fontSize: "48px", mb: 2 }} />
        <Typography variant="h6">No Suggested Matches</Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          There are no suggested matches available for this transaction.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <Box sx={styles.header.wrapper}>
        <Box sx={styles.header.titleRow}>
          <Box sx={styles.header.titleBox}>
            <InfoOutlinedIcon />
            <Typography variant="subtitle1" sx={styles.header.title}>
              Suggested Matches
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={styles.header.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.header.subtitle}>
          Review and select a match for this bank transaction
        </Typography>
      </Box>

      <DialogContent>
        <Box sx={styles.transactionDetails.container}>
          <Typography variant="subtitle1" gutterBottom>
            Bank Transaction Details
          </Typography>
          <Box sx={styles.transactionDetails.row}>
            <Typography
              variant="subtitle2"
              sx={styles.transactionDetails.label}
            >
              Date:
            </Typography>
            <Typography sx={styles.transactionDetails.value}>
              {formatDate(transaction.bankDate)}
            </Typography>
          </Box>
          <Box sx={styles.transactionDetails.row}>
            <Typography
              variant="subtitle2"
              sx={styles.transactionDetails.label}
            >
              Description:
            </Typography>
            <Typography sx={styles.transactionDetails.value}>
              {transaction.description}
            </Typography>
          </Box>
          <Box sx={styles.transactionDetails.row}>
            <Typography
              variant="subtitle2"
              sx={styles.transactionDetails.label}
            >
              Amount:
            </Typography>
            <Typography
              sx={styles.transactionDetails.value}
              color="primary"
              variant="subtitle2"
            >
              {formatCurrency(transaction.amount)}
            </Typography>
          </Box>
        </Box>

        {renderContent()}
      </DialogContent>

      {!isLoading && !isAutoMatched && (
        <Box sx={styles.footer.wrapper}>
          <Box sx={styles.footer.helpText}>
            <InfoOutlinedIcon sx={styles.footer.iconInfo} />
            <Typography variant="body2">
              Can&apos;t find the right match? Mark this transaction as:
            </Typography>
          </Box>
          <Box sx={styles.footer.buttonGroup}>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePostedOutside}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={16} /> : undefined
              }
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  borderColor: "primary.dark",
                  backgroundColor: "primary.main",
                  color: "white",
                },
              }}
            >
              Posted Outside Copilot
            </Button>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={handleReportMissing}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={16} /> : undefined
              }
            >
              Report Missing
            </Button>
            {devMode && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleMarkDuplicate}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? <CircularProgress size={16} /> : undefined
                }
              >
                Duplicate
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
